import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  
  UserCheck,
  Mail,
  ArrowLeft,
} from "lucide-react";

import "./LegalPages.css";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="legal-page">

      {/* =========================
          HERO / PAGE HEADER
      ========================== */}
      <section className="legal-hero">
        <div className="legal-container">
          <div className="legal-hero-content">

            <div className="legal-icon">
              <ShieldCheck size={34} />
            </div>

            <h1>Privacy Policy</h1>

            <p>
              Your privacy is important to us. This Privacy Policy explains
              how SHREE BRAINS collects, uses, protects, and manages your
              information when you use our website and services.
            </p>

            <span className="legal-updated">
              Last Updated: September 21, 2026
            </span>

          </div>
        </div>
      </section>


      {/* =========================
          CONTENT
      ========================== */}
      <section className="legal-content-section">
        <div className="legal-container">

          <div className="legal-content">

            {/* Introduction */}
            <section className="legal-section">
              <h2>1. Introduction</h2>

              <p>
                SHREE BRAINS ("we", "our", "us", or "SHREE BRAINS") respects
                your privacy and is committed to protecting the personal
                information you share with us.
              </p>

              <p>
                This Privacy Policy describes how we collect, use, store,
                process, and protect information when you visit our website,
                communicate with us, or use our software, e-commerce,
                business automation, SMS, email, WhatsApp Business API,
                and other digital services.
              </p>

              <p>
                By using our website or services, you acknowledge that you
                have read and understood this Privacy Policy.
              </p>
            </section>


            {/* Information Collection */}
            <section className="legal-section">
              <h2>2. Information We Collect</h2>

              <p>
                Depending on how you interact with us, we may collect the
                following types of information:
              </p>

              <h3>2.1 Personal Information</h3>

              <ul>
                <li>Full name</li>
                <li>Business or company name</li>
                <li>Email address</li>
                <li>Mobile or telephone number</li>
                <li>Business address</li>
                <li>Billing and communication information</li>
                <li>Information provided through contact forms</li>
              </ul>

              <h3>2.2 Business Information</h3>

              <ul>
                <li>Business requirements</li>
                <li>Order and customer-management information</li>
                <li>Product and service information</li>
                <li>Information required to configure our software</li>
                <li>Other information voluntarily provided for business services</li>
              </ul>

              <h3>2.3 Technical Information</h3>

              <p>
                When you visit our website, certain technical information
                may be collected automatically, such as:
              </p>

              <ul>
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device information</li>
                <li>Operating system</li>
                <li>Pages visited</li>
                <li>Approximate access time</li>
                <li>Website usage information</li>
              </ul>
            </section>


            {/* How Information Is Used */}
            <section className="legal-section">
              <h2>3. How We Use Your Information</h2>

              <p>
                We may use collected information for legitimate business
                purposes, including:
              </p>

              <ul>
                <li>Providing and maintaining our services</li>
                <li>Responding to enquiries and support requests</li>
                <li>Understanding your business requirements</li>
                <li>Providing quotations and service information</li>
                <li>Processing service-related communications</li>
                <li>Improving our website and services</li>
                <li>Managing customer relationships</li>
                <li>Maintaining security and preventing misuse</li>
                <li>Sending important service-related notifications</li>
                <li>Complying with applicable legal obligations</li>
              </ul>
            </section>


            {/* Communication */}
            <section className="legal-section">
              <h2>4. Communication and Marketing</h2>

              <p>
                If you provide your contact details, we may contact you
                regarding enquiries, services, technical support, account
                information, or business communications.
              </p>

              <p>
                Where permitted by applicable law, we may also send
                information about our products, services, offers, or
                updates. You may request that we stop sending promotional
                communications at any time.
              </p>
            </section>


            {/* Third Party */}
            <section className="legal-section">
              <h2>5. Third-Party Services</h2>

              <p>
                Depending on the services being provided, SHREE BRAINS may
                work with third-party service providers such as hosting
                providers, communication platforms, payment providers,
                analytics services, email providers, SMS providers, or
                WhatsApp Business API providers.
              </p>

              <p>
                Such third parties may process information only to the
                extent necessary to provide their respective services and
                are expected to handle information according to applicable
                privacy and security requirements.
              </p>
            </section>


            {/* Data Security */}
            <section className="legal-section">
              <div className="legal-highlight">
                <Lock size={24} />

                <div>
                  <h3>6. Data Security</h3>

                  <p>
                    We take reasonable technical and organizational measures
                    to protect information against unauthorized access,
                    alteration, disclosure, misuse, or destruction.
                  </p>
                </div>
              </div>

              <p>
                However, no internet transmission, electronic storage system,
                or online service can be guaranteed to be completely secure.
                Therefore, while we take reasonable precautions, we cannot
                guarantee absolute security of information transmitted to us
                electronically.
              </p>
            </section>


            {/* Data Retention */}
            <section className="legal-section">
              <h2>7. Data Retention</h2>

              <p>
                We retain information only for as long as reasonably
                necessary for the purposes described in this Privacy Policy,
                including providing services, maintaining business records,
                resolving disputes, enforcing agreements, and complying with
                applicable legal requirements.
              </p>
            </section>


            {/* User Rights */}
            <section className="legal-section">
              <div className="legal-highlight">
                <UserCheck size={24} />

                <div>
                  <h3>8. Your Privacy Rights</h3>

                  <p>
                    Depending on applicable law, you may have rights regarding
                    your personal information, including requesting access,
                    correction, updating, or deletion of certain information.
                  </p>
                </div>
              </div>

              <p>
                To make a privacy-related request, please contact us using
                the contact details provided at the end of this policy.
              </p>
            </section>


            {/* Cookies */}
            <section className="legal-section">
              <h2>9. Cookies and Website Technologies</h2>

              <p>
                Our website may use cookies or similar technologies to
                improve functionality, understand website usage, remember
                preferences, and improve user experience.
              </p>

              <p>
                You may configure your browser to refuse or restrict cookies.
                However, disabling certain cookies may affect some website
                functionality.
              </p>
            </section>


            {/* Children */}
            <section className="legal-section">
              <h2>10. Children's Privacy</h2>

              <p>
                Our services are primarily intended for businesses and
                general users. We do not knowingly collect personal
                information from children for purposes that are prohibited
                by applicable law.
              </p>
            </section>


            {/* External Links */}
            <section className="legal-section">
              <h2>11. External Websites</h2>

              <p>
                Our website may contain links to third-party websites or
                services. SHREE BRAINS is not responsible for the privacy
                practices, content, or security of those external websites.
              </p>

              <p>
                We recommend reviewing the privacy policies of third-party
                websites before providing them with personal information.
              </p>
            </section>


            {/* Changes */}
            <section className="legal-section">
              <h2>12. Changes to This Privacy Policy</h2>

              <p>
                We may update this Privacy Policy from time to time to
                reflect changes in our services, technology, legal
                requirements, or business practices.
              </p>

              <p>
                Any updated version will be published on this page with a
                revised "Last Updated" date.
              </p>
            </section>


            {/* Contact */}
            <section className="legal-section legal-contact-section">
              <div className="legal-contact-icon">
                <Mail size={28} />
              </div>

              <h2>13. Contact Us</h2>

              <p>
                If you have questions, concerns, or requests regarding this
                Privacy Policy or the handling of your information, please
                contact us.
              </p>

              <div className="legal-contact-details">
                <strong>SHREE BRAINS</strong>

                <span>New Delhi, India</span>
<span> </span>
                <a href="mailto:info@shreebrains.com">
                  info@shreebrains.com  
                </a>
<span> </span>
                <a href="tel:+919971815737">
                  +91 99718 15737
                </a>
              </div>
            </section>


            {/* Back */}
            <div className="legal-back">
              <Link to="/">
                <ArrowLeft size={17} />
                Back to Home
              </Link>
            </div>

          </div>
        </div>
      </section>

    </main>
  );
};

export default PrivacyPolicy;